import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { API_URL, formatCurrency } from '../utils/formatters';

/**
 * Splits of uploaded transactions / workbook cashflows (user 2026-10-15:
 * Manage Splits only listed bank splits, so these could never be undone).
 * Backend: GET  /admin/investor-transactions/reconciliation/uploaded-splits
 *          POST /admin/investor-transactions/reconciliation/undo-uploaded-split
 * Undo deletes the parts and restores the original, unmatched; every part
 * must be unmatched first.
 */
export function UploadedSplitsSection({ open, headers, reconInvestorFilter, onUndone }) {
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/investor-transactions/reconciliation/uploaded-splits`, { headers });
      setSplits(res.data?.splits || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load uploaded splits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSearch(reconInvestorFilter && reconInvestorFilter !== 'all' ? reconInvestorFilter : '');
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const undo = async (s) => {
    const r = s.restore || {};
    const parts = s.parts.map(p => formatCurrency(p.amount)).join(' + ');
    if (!window.confirm(
      `Undo this split?\n\nDeletes the ${s.parts.length} parts (${parts}) and restores the ${r.what} `
      + `${formatCurrency(r.amount)} · ${r.date || ''} · ${r.loan_id || ''} as UNMATCHED.`
    )) return;
    setBusyId(s.split_from);
    try {
      const res = await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/undo-uploaded-split`,
        { split_from: s.split_from }, { headers });
      toast.success(res.data?.message || 'Split undone');
      setSplits(prev => prev.filter(x => x.split_from !== s.split_from));
      if (onUndone) onUndone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to undo the split');
    } finally {
      setBusyId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const shown = q
    ? splits.filter(s => [s.investor_id, s.loan_id, s.transaction_type, s.transaction_date,
        ...s.parts.map(p => p.reference)].some(v => String(v || '').toLowerCase().includes(q)))
    : splits;

  return (
    <div className="space-y-2" data-testid="uploaded-splits-section">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-sm">Uploaded transaction splits</h3>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter: investor, loan, reference…"
          className="h-8 w-64" data-testid="uploaded-splits-search" />
        <span className="text-xs text-slate-500">{shown.length} of {splits.length}</span>
      </div>
      <p className="text-xs text-slate-500">
        Undo deletes the parts and restores the original transaction or workbook cashflow, unmatched.
        Unmatch or unallocate every part first.
      </p>
      {loading ? (
        <div className="py-6 text-center text-slate-500">Loading&hellip;</div>
      ) : shown.length === 0 ? (
        <div className="py-6 text-center text-slate-500">No uploaded transaction splits{q ? ' match the filter' : ''}.</div>
      ) : (
        <div className="border rounded overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Investor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Loan / type</TableHead>
                <TableHead className="text-right">Original</TableHead>
                <TableHead>Parts</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map(s => (
                <TableRow key={s.split_from} data-testid={`uploaded-split-row-${s.split_from}`}>
                  <TableCell className="text-xs font-mono">{s.investor_id || '—'}</TableCell>
                  <TableCell className="text-xs">{s.transaction_date || '—'}</TableCell>
                  <TableCell className="text-xs">
                    <div className="font-mono">{s.loan_id || '—'}</div>
                    <div className="text-slate-500">{s.transaction_type}{s.kind === 'cashflow' ? ' · workbook cashflow' : ''}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{s.restore ? formatCurrency(s.restore.amount) : '—'}</TableCell>
                  <TableCell className="text-xs">
                    {s.parts.map(p => (
                      <div key={p.id} className={`font-mono ${p.busy ? 'text-amber-700' : ''}`}>
                        {formatCurrency(p.amount)}{p.split_kind === 'non_cash' ? ' (non-cash)' : ''}{p.busy ? ' · matched' : ''}
                      </div>
                    ))}
                    {!s.can_undo && s.reason && <div className="text-[11px] text-amber-700 mt-1 max-w-[260px]">{s.reason}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={!s.can_undo || busyId === s.split_from}
                      onClick={() => undo(s)}
                      className="text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-40"
                      data-testid={`undo-uploaded-split-${s.split_from}`}
                      title={s.can_undo ? 'Delete the parts and restore the original, unmatched' : s.reason}>
                      {busyId === s.split_from ? '…' : 'Undo split'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default UploadedSplitsSection;
